/** PATCH helper for the bus-stop editor. Stops carry `updated_ts`, so writes
 *  send an `expected_updated_ts` optimistic-concurrency token; a 409
 *  (`route_stop_modified`) sets the conflict flag for the ConflictBanner. Sends
 *  multipart when an image File is staged (to set/replace the image), else plain
 *  JSON. */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as routeStopsApi from '@/api/routeStops';
import type { RouteStopDetail, RouteStopWriteJson } from '@/api/routeStops';
import { useSnackbar } from '@/components/Snackbar';

export function useRouteStopPatch(stop: RouteStopDetail, onSaved: () => void) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [conflict, setConflict] = useState(false);

  const mutation = useMutation({
    mutationFn: ({ body, image }: { body: RouteStopWriteJson; image?: File | null }) => {
      const payload = { ...body, expected_updated_ts: stop.updated_ts };
      return routeStopsApi.update(stop.id, payload, image);
    },
    onSuccess: () => {
      setConflict(false);
      snackbar.show('Saved.', 'success');
      qc.invalidateQueries({ queryKey: ['route_stops'] });
      onSaved();
    },
    onError: (e) => {
      if (e instanceof ConsoleApiError && e.status === 409) {
        setConflict(true);
        return;
      }
      const msg = e instanceof ConsoleApiError ? e.message : 'Save failed.';
      snackbar.show(msg, 'error');
    },
  });

  return {
    save: (body: RouteStopWriteJson, image?: File | null) => mutation.mutate({ body, image }),
    saving: mutation.isPending,
    conflict,
    dismissConflict: () => setConflict(false),
    /** Field-keyed server errors from a 400, if any. */
    fieldErrors:
      mutation.error instanceof ConsoleApiError && mutation.error.status === 400
        ? (mutation.error.body as Record<string, unknown>)
        : null,
  };
}
